
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useImageUpload = (onImageSelected: (url: string) => void, currentImageUrl?: string) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageUrl, setImageUrl] = useState(currentImageUrl || "");
  const [session, setSession] = useState(null);

  useEffect(() => {
    if (currentImageUrl) {
      setImageUrl(currentImageUrl);
    }
  }, [currentImageUrl]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const clearImageUrl = () => {
    setImageUrl("");
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!session) {
      toast({
        title: "Error",
        description: "You must be logged in to upload images",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      setImageUrl(publicUrl);
      onImageSelected(publicUrl);
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Error",
        description: `Failed to upload image: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt for the image",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-image', {
        body: { prompt: imagePrompt }
      });

      if (error) throw error;
      if (!data?.image) throw new Error('No image data received from the server');

      setPreviewUrl(data.image);
      toast({
        title: "Success",
        description: "Image generated successfully. Review the preview below.",
      });
    } catch (error) {
      console.error("Error generating image:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate image",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptGeneratedImage = async () => {
    if (!previewUrl) return;

    setIsUploading(true);
    try {
      // For data URLs, convert to Blob using Canvas
      const blob = await dataURLToBlob(previewUrl);
      const fileName = `ai-generated-${Date.now()}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(fileName, blob, {
          contentType: 'image/png'
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(fileName);

      setImageUrl(publicUrl);
      onImageSelected(publicUrl);
      setPreviewUrl("");
      toast({
        title: "Success",
        description: "Generated image saved successfully",
      });
    } catch (error) {
      console.error("Error saving generated image:", error);
      toast({
        title: "Error",
        description: `Failed to save generated image: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveToGallery = async () => {
    if (!previewUrl) return;

    setIsUploading(true);
    try {
      // For data URLs, convert to Blob using Canvas
      const blob = await dataURLToBlob(previewUrl);
      const fileName = `ai-generated-${Date.now()}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(fileName, blob, {
          contentType: 'image/png'
        });

      if (uploadError) throw uploadError;

      // Note: We do not set imageUrl or call onImageSelected here
      setPreviewUrl("");
      toast({
        title: "Success",
        description: "Image saved to gallery without attaching to article",
      });
    } catch (error) {
      console.error("Error saving to gallery:", error);
      toast({
        title: "Error",
        description: `Failed to save to gallery: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRejectGeneratedImage = () => {
    setPreviewUrl("");
    toast({
      title: "Info",
      description: "Generated image discarded",
    });
  };

  // Helper function to convert data URL to Blob
  const dataURLToBlob = async (dataURL: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        // Create an image element to load the data URL
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          // Create a canvas element to draw the image
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw the image on the canvas
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get 2D context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          // Convert the canvas to blob
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          }, 'image/png');
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image from data URL'));
        };
        
        // Set the data URL as the image source
        img.src = dataURL;
      } catch (error) {
        reject(error);
      }
    });
  };

  return {
    isUploading,
    isGenerating,
    imagePrompt,
    previewUrl,
    imageUrl,
    setImagePrompt,
    handleImageUpload,
    handleGenerateImage,
    handleAcceptGeneratedImage,
    handleSaveToGallery,
    handleRejectGeneratedImage,
    clearImageUrl,
  };
};
